import { C } from '@angular/cdk/keycodes';
import {
    merkle,
    Operation,
    SalesDocument,
    SalesDocumentPosition,
    SalesDocumentPositionPrices,
    DocumentAttachment,
    SalesDocumentConnection,
    SalesDocumentConnections,
    SalesDocumentPositionBasePrices,
    Delivery,
    State,
    DocumentStatusSystem,
    DocumentStatusSystems,
    PartialOperation,
    Synced,
    ChangeTag,
    IccDocument,
    IccDocumentPosition,
    DocumentType,
    ComplaintSparePart,
    ComplaintNote,
} from '@icc/b2b/shared';
import { Actions } from '@ngrx/effects';
import { Action, createAction, props } from '@ngrx/store';
import { ComplaintPosition } from 'libs/b2b/shared/src/lib/models/complaint-position.model';
import { ComplaintAppointment } from 'libs/b2b/shared/src/lib/models/complaint-appointment.model';

export const init = createAction('[SalesDocuments Page] Init');

export const loadSalesDocumentsSuccess = createAction(
    '[SalesDocuments/API] Load SalesDocuments Success',
    props<{
        documents: Record<string, IccDocument>;
        documentsIds: string[];
        positions: Record<string, IccDocumentPosition>;
        appointments: Record<string, ComplaintAppointment>;
        complaintNotes: Record<string, ComplaintNote>;
        complaintSpareParts: Record<string, ComplaintSparePart>;
        attachments: DocumentAttachment[];
        deliveries: Record<string, Delivery>;
        documentConnections: SalesDocumentConnections;
        ops: Operation[];
        operationsKeysMap: Map<string, Operation | PartialOperation>;
        operationsIds: Map<string, true>;
        selectedId: string;
        userId: number;
        userCompanyId: number;
        userMarketId: number;
        userEmail: string;
        userPhoneNumber: string;
        userReferenceName: string;
        machineId: string;
    }>()
);

export const loadSalesDocumentsFailure = createAction(
    '[SalesDocuments/API] Load SalesDocuments Failure',
    props<{ error: any }>()
);

export const addDoc = createAction('[SalesDocuments/API] Add Doc', props<{ doc: IccDocument }>());
export const addDocWithPosition = createAction(
    '[SalesDocuments/API] Add Doc With Position',
    props<{ doc: IccDocument; position: IccDocumentPosition }>()
);
export const changeDoc = createAction(
    '[SalesDocuments/API] Rename Doc',
    props<{ id: IccDocument['uuid']; changes: Partial<IccDocument>; onlySales?: boolean }>()
);
export const addPosition = createAction(
    '[SalesDocuments/API] Add Position',
    props<{ position: IccDocumentPosition; onlySales?: boolean }>()
);
export const addAppointment = createAction(
    '[SalesDocuments/API] Add Appointment',
    props<{ appointment: ComplaintAppointment }>()
);
export const addComplaintSparePart = createAction(
    '[SalesDocuments/API] Add Complaint Spare Part',
    props<{ complaintSparePart: ComplaintSparePart }>()
);
export const addComplaintNote = createAction(
    '[SalesDocuments/API] Add Complaint Note',
    props<{ complaintNote: ComplaintNote }>()
);
export const changePosition = createAction(
    '[SalesDocuments/API] Change Position',
    props<{
        id: IccDocumentPosition['uuid'];
        docId: IccDocumentPosition['documentId'];
        changes: Partial<Omit<SalesDocumentPosition, Exclude<keyof SalesDocumentPositionPrices, 'priceCalculationDate' | 'validityDate'> | keyof SalesDocumentPositionBasePrices>> | Partial<ComplaintPosition>;
        onlySales?: boolean;
    }>()
);
export const changeAppointment = createAction(
    '[SalesDocuments/API] Change Appointment',
    props<{ id: ComplaintAppointment['uuid']; docId: ComplaintAppointment['documentId']; changes: Partial<ComplaintAppointment> }>()
);
export const changeComplaintSparePart = createAction(
    '[SalesDocuments/API] Change Complaint Spare Part',
    props<{ id: ComplaintSparePart['uuid']; docId: ComplaintSparePart['documentId']; changes: Partial<ComplaintSparePart> }>()
);

export const changeAttachment = createAction(
    '[SalesDocuments/API] Change Attachment',
    props<{
        id: DocumentAttachment['uuid'];
        docId: DocumentAttachment['documentId'];
        changes: Partial<DocumentAttachment>;
        onlySales?: boolean;
    }>()
);

export const changePositionSlaves = createAction(
    '[SalesDocuments/API] Change Position Slaves',
    props<{
        id: IccDocumentPosition['uuid'];
        docId: IccDocumentPosition['documentId'];
        slaves: IccDocumentPosition['slaves'];
        buyPositions: IccDocumentPosition[];
        buyDocument: IccDocument;
    }>()
);

export const changePositionPrice = createAction(
    '[SalesDocuments/API] Change Position Price',
    props<{
        id: SalesDocumentPosition['uuid'];
        docId: SalesDocumentPosition['documentId'];
        changes: SalesDocumentPositionPrices;
    }>()
);

export const changePositionBasePrice = createAction(
    '[SalesDocuments/API] Change Position Base Price',
    props<{
        id: SalesDocumentPosition['uuid'];
        docId: SalesDocumentPosition['documentId'];
        changes: SalesDocumentPositionBasePrices;
        onlySales?: boolean;
    }>()
);

export const openDoc = createAction(
    '[SalesDocuments/API] Open Doc',
    props<{ id: SalesDocument['uuid'] }>()
);

export const joinDocuments = createAction(
    '[SalesDocuments/API] Join Documents',
    props<{ uuid: string, salesDocumentId: string; buyDocumentId: string }>()
);

export const detachDocuments = createAction(
    '[SalesDocuments/API] Detach Documents',
    props<{ salesDocumentId: string; buyDocumentId: string }>()
);

export const joinPositions = createAction(
    '[SalesDocuments/API] Join Positions',
    props<{
        uuid: string;
        salesDocumentId: string;
        buyDocumentId: string;
        salesPositionId: string;
        buyPositionId: string;
    }>()
);

export const detachPositions = createAction(
    '[SalesDocuments/API] Detach Positions',
    props<{
        salesDocumentId: string;
        buyDocumentId: string;
        salesPositionId: string;
        buyPositionId: string;
    }>()
);

export const addAttachment = createAction(
    '[SalesDocuments/API] Add Attachment',
    props<{ attachment: DocumentAttachment }>()
);

export const joinAttachments = createAction(
    '[SalesDocuments/API] Join Attachments',
    props<{
        uuid: string;
        salesDocumentId: string;
        buyDocumentId: string;
        salesAttachmentId: string;
        buyAttachmentId: string;
    }>()
);

export const removeAttachment = createAction(
    '[SalesDocuments/API] Remove Attachment',
    props<{
        id: DocumentAttachment['uuid'];
        docId: DocumentAttachment['documentId'];
        isDeleted: true;
    }>()
);

export const addDelivery = createAction(
    '[SalesDocuments/API] Add Delivery',
    props<{ position: SalesDocumentPosition; onlySales?: boolean }>()
);
export const changeDelivery = createAction(
    '[SalesDocuments/API] Change Delivery',
    props<{
        id: Delivery['uuid'];
        docId: Delivery['documentId'];
        changes: Partial<Delivery>;
    }>()
);
export const removeAppointment = createAction(
    '[SalesDocuments/API] Remove Appointment',
    props<{
        id: ComplaintAppointment['uuid'];
        docId: ComplaintAppointment['documentId'];
        isDeleted: true;
    }>()
);
export const removeComplaintSparePart = createAction(
    '[SalesDocuments/API] Remove Complaint Spare Part',
    props<{
        id: ComplaintSparePart['uuid'];
        docId: ComplaintSparePart['documentId'];
        complaintSparePart: ComplaintSparePart;
        isDeleted: true;
    }>()
);

export const splitedDocument = createAction(
    '[SalesDocuments/API] Splited Document',
    props<{
        id: string;
        docId: string;
        changes: {documentsUuids: string[]};
    }>()
);

export const tagDoc = createAction(
    '[SalesDocuments/API] Tag Document History',
    props<{
        docId: string;
        changeTag: ChangeTag;
    }>()
);

export const tagsSavedInDb = createAction(
    '[SalesDocuments/API] Tags Saved In DB',
    props<{
        savedTags: ChangeTag[];
    }>()
);

export const sendOpsToWorker = createAction(
    '[SalesDocuments/API] Send Ops To Worker',
    props<{
        ops: Operation[];
        staleDocs: Map<string, number>;
        graphsToSend: Map<string, number>;
        syncKey?: number;
    }>()
);

export const opsSyncedOnWorker = createAction('[SalesDocuments/API] Ops Synced On Worker', props<{
    ops: Operation[],
    fromBroadcast?: boolean;
}>());
export const updatedDocumentsOnWorker = createAction('[SalesDocuments/API] Updated Documents On Worker', props<{
    updates: {
        documents: Record<string, IccDocument>;
        documentsIds: string[];
        positions: Record<string, IccDocumentPosition>;
        appointments: Record<string, ComplaintAppointment>;
        complaintNotes: Record<string, ComplaintNote>;
        complaintSpareParts: Record<string, ComplaintSparePart>;
        attachments: DocumentAttachment[];
        deliveries: Record<string, Delivery>;
        documentConnections: SalesDocumentConnection[];
        ops: Operation[];
        graphs: Record<string, merkle.MerkleTrie>;
        updatedGraphs: Record<string, number>;
        updatedGraphsSent: Record<string, number>;
        deletedDocuments: string[];
    },
    fromBroadcast?: boolean;
}>());
export const deletedDocumentsOnWorker = createAction('[SalesDocuments/API] Deleted Documents On Worker', props<{
    updates: {
        deletedDocuments: string[];
    },
}>());

export const loadedDocumentsFromDB = createAction('[SalesDocuments/API] Loaded Documents From DB', props<{
    updates: {
        documents: Record<string, IccDocument>;
        documentsIds: string[];
        documentTypes: Map<string, DocumentType>;
        positions: Record<string, IccDocumentPosition>;
        appointments: Record<string, ComplaintAppointment>;
        complaintNotes: Record<string, ComplaintNote>;
        complaintSpareParts: Record<string, ComplaintSparePart>;
        attachments: DocumentAttachment[];
        deliveries: Record<string, Delivery>;
        documentConnections: SalesDocumentConnection[];
        ops: Operation[];
    },
}>());

export const updatedSyncedStateOnWorker = createAction('[SalesDocuments/API] Updated Synced State On Worker', props<{
    synced: Map<string, Synced>;
    staleDocs?: Map<string, number>;
    changedDocs?: string[];
    fromBroadcast?: boolean;
}>());

export const mergeOps = createAction('[SalesDocuments/API] Merge Ops',
    props<{
        operations: Operation[],
        statusSystems: DocumentStatusSystems;
        selectedId?: string | number;
        load?: boolean;
        syncKey?: number;
        /** If true, these operations came from BroadcastChannel - don't re-broadcast or save to IDB */
        fromBroadcast?: boolean;
     }>()
);

export const startBuffering = createAction('[Buffer] Start Buffering');
export const stopBuffering = createAction('[Buffer] Stop Buffering');
export const noop = createAction('Noop');
